import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Cog6ToothIcon, BellIcon, SunIcon, MoonIcon, ArrowRightOnRectangleIcon, PaintBrushIcon, BookOpenIcon,
  ClipboardDocumentCheckIcon, ArrowPathIcon, CreditCardIcon, UserPlusIcon, PlusIcon,
} from '@heroicons/react/24/outline'
import { API_BASE_URL } from '../config/api'
import { isCommunity } from '../config/edition'
import { fetchCsrfToken, getCsrfToken, csrfHeader, clearCsrfToken } from '../lib/csrf'
import { C, getTheme, toggleTheme } from '../theme'
import '../styles/patterns.css'
import '../styles/dev-portal.css'
import { AnalyticsCharts } from '../components/developer/AnalyticsCharts'
import { DeveloperOverview, DismissibleTeamBanner } from '../components/developer/DeveloperOverview'
import { BillingSection } from '../components/billing/BillingSection'
import { DashRoot, IconButton, Dropdown, PillButton, PillLink } from '../components/dashboard/primitives'
import { TopNav, PageHead } from '../components/dashboard/Layout'
import { getWallet, type Wallet } from '../lib/billingApi'
import { formatMoney } from '../lib/money'
import { fetchDashboardProfile } from '../lib/operatorSession'
import type { OperatorBlock } from '../lib/operatorSession'

import type { ApiKey, DeveloperStats } from '../components/developer/types'
import { AuthGate } from '../components/developer/AuthGate'
import { ApiKeysSection } from '../components/developer/ApiKeysSection'
import { WebhooksSection } from '../components/developer/WebhooksSection'
import { SettingsModal } from '../components/developer/SettingsModal'

const portalPages = [
  { path: '/developer', title: 'Dashboard', description: 'An overview of your verification activity and developer tools.' },
  { path: '/developer/analytics', title: 'Analytics', description: 'Monitor verification volume, performance, and webhook deliveries.' },
  { path: '/developer/api-keys', title: 'API Keys', description: 'Create and manage your verification API keys.' },
  { path: '/developer/webhooks', title: 'Webhooks', description: 'Configure notifications for your verification events.' },
  { path: '/developer/billing', title: 'Billing', description: 'Your prepaid wallet, top-ups, applicant fees and transactions.' },
]


export function DeveloperPage() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const activePage = portalPages.find(page => page.path === pathname) ?? portalPages[0]
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [profile, setProfile] = useState<{ name?: string | null; email?: string | null; avatar_url?: string | null; company?: string | null } | null>(null)
  const [wallet, setWallet] = useState<Wallet | null>(null)

  const [token, setToken] = useState<string | null>(() => getCsrfToken() ? 'session' : null)
  const [setupNeeded, setSetupNeeded] = useState<boolean | null>(null)
  const [theme, setTheme] = useState<'light' | 'dark'>(() => getTheme())
  const [isOperator, setIsOperator] = useState(false)
  const [operator, setOperator] = useState<OperatorBlock | null>(null)

  // On mount, check if an auth cookie exists by probing a protected endpoint.
  // Uses fetchDashboardProfile() to also detect operator mode in one call.
  useEffect(() => {
    fetchDashboardProfile()
      .then(result => {
        if (result.authed) {
          setToken('session')
          setIsOperator(result.isOperator)
          setOperator(result.operator)
          setProfile(result.raw?.data ?? (result.operator ? { name: result.operator.service_label || result.operator.email, email: result.operator.email } : null))
          fetchCsrfToken()
        } else {
          setToken(null)
          clearCsrfToken()
        }
      })
      .catch(() => {
        // Network error — leave token state unchanged; isOperator stays false
      })
  }, [])

  // Community edition: redirect to /setup if no developers exist yet.
  // On API error (e.g. backend not ready during Docker startup), redirect
  // to /setup which has a retry UI, rather than showing the login form
  // for an account that doesn't exist yet.
  useEffect(() => {
    if (token || !isCommunity) { setSetupNeeded(false); return }
    fetch(`${API_BASE_URL}/api/setup/status`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(data => {
        if (data.needs_setup) navigate('/setup', { replace: true })
        else setSetupNeeded(false)
      })
      .catch(() => navigate('/setup', { replace: true }))
  }, [token, navigate])

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [stats, setStats] = useState<DeveloperStats | null>(null)
  const [newFullKey, setNewFullKey] = useState<string | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [hasOrgAdmin, setHasOrgAdmin] = useState<boolean | null>(null)
  const [teamBannerDismissed, setTeamBannerDismissed] = useState(
    () => localStorage.getItem('saflens:team-banner-dismissed') === 'true'
  )

  const authHeaders = (t: string) =>
    t === 'session' ? {} as Record<string, string> : { Authorization: `Bearer ${t}` } as Record<string, string>

  const fetchKeys = async () => {
    if (!token) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/developer/api-keys`, {
        headers: authHeaders(token),
        credentials: 'include' as RequestCredentials,
      })
      if (res.status === 401) { handleUnauthorized(); return }
      if (res.ok) setApiKeys((await res.json()).api_keys ?? [])
    } catch { /* network error */ }
  }

  const fetchStats = async () => {
    if (!token) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/developer/stats`, {
        headers: authHeaders(token),
        credentials: 'include' as RequestCredentials,
      })
      if (res.ok) setStats(await res.json())
    } catch { /* network error */ }
  }

  const checkOrgAdmin = async () => {
    if (!token) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/developer/reviewers`, {
        headers: authHeaders(token),
        credentials: 'include' as RequestCredentials,
      })
      if (res.ok) {
        const data = await res.json()
        const admins = (data.reviewers || []).filter((r: any) => r.role === 'admin' && r.status !== 'revoked')
        setHasOrgAdmin(admins.length > 0)
      }
    } catch { /* network error */ }
  }

  // Service-operator sessions are not accepted on /api/developer/billing/*.
  const fetchWallet = useCallback(() => {
    if (isOperator) { setWallet(null); return }
    getWallet().then(setWallet).catch(() => setWallet(null))
  }, [isOperator])

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/developer/profile`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        if (data?.data) setProfile(data.data)
      }
    } catch { /* network error */ }
  }

  useEffect(() => {
    if (token) {
      fetchKeys()
      fetchStats()
      checkOrgAdmin()
      if (!profile) fetchProfile()
    }
  }, [token])

  useEffect(() => { if (token) fetchWallet() }, [token, fetchWallet])

  const handleAuth = (t: string, apiKey?: string) => {
    setIsOperator(false)
    setOperator(null)
    setToken(t)
    fetchCsrfToken()
    if (apiKey) setNewFullKey(apiKey)
  }

  const handleLogout = () => {
    fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...csrfHeader() }
    }).catch(() => {})
    clearCsrfToken()
    setToken(null)
    setApiKeys([])
    setStats(null)
    setIsOperator(false)
    setOperator(null)
  }

  const handleAccountDeleted = () => {
    setToken(null)
    setApiKeys([])
    setStats(null)
    setIsOperator(false)
    setOperator(null)
  }

  const handleUnauthorized = () => {
    setToken(null)
    setIsOperator(false)
    setOperator(null)
  }

  const handleToggleTheme = () => setTheme(toggleTheme())

  if (!token) {
    if (isCommunity && setupNeeded === null) {
      return (
        <div style={{ background: C.bg, fontFamily: C.sans, color: C.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: C.muted, fontSize: 14 }}>Loading...</div>
        </div>
      )
    }
    return (
      <div style={{ background: C.bg, fontFamily: C.sans, color: C.text, minHeight: '100vh' }}>
        <AuthGate onAuth={handleAuth} />
      </div>
    )
  }

  const navPath = pathname === '/' ? '/developer' : pathname
  const lowBalance = !!(wallet?.enabled && wallet.low_balance && !isOperator)
  const teamPending = !isOperator && hasOrgAdmin === false
  const notifications = [
    ...(lowBalance ? [{ key: 'balance', icon: <CreditCardIcon />, title: `Low wallet balance — ${formatMoney(wallet!.balance_cents)}`, sub: 'Top up to keep live verifications running', to: '/developer/billing' }] : []),
    ...(teamPending ? [{ key: 'team', icon: <UserPlusIcon />, title: 'Invite an organisation admin', sub: 'Let your team review verifications', onClick: () => setShowSettings(true) }] : []),
    ...(apiKeys.length === 0 ? [{ key: 'keys', icon: <PlusIcon />, title: 'Create your first API key', sub: 'You need a key to start verifications', to: '/developer/api-keys' }] : []),
  ]
  const refreshAll = () => { fetchKeys(); fetchStats(); fetchWallet(); setRefreshVersion(version => version + 1) }

  const topActions = (
    <>
      <IconButton className="sf-hide-sm" label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} onClick={handleToggleTheme}>
        {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
      </IconButton>
      <IconButton className="sf-hide-sm" label="Settings" onClick={() => setShowSettings(true)}><Cog6ToothIcon /></IconButton>
      <Dropdown align="right" trigger={({ toggle }) => (
        <IconButton label="Notifications" dot={notifications.length > 0} onClick={toggle}><BellIcon /></IconButton>
      )}>
        {close => (
          <div style={{ width: 300 }}>
            <div className="sf-dd-label">Notifications</div>
            {notifications.length === 0 && <div style={{ padding: '10px 12px 14px', color: 'var(--sf-mid)', fontSize: 13.5 }}>You're all caught up.</div>}
            {notifications.map(n => (
              <button key={n.key} type="button" onClick={() => { close(); if (n.to) navigate(n.to); else n.onClick?.() }} style={{ alignItems: 'flex-start' }}>
                <span className="sf-roundicon accent" style={{ width: 34, height: 34 }}>{n.icon}</span>
                <span><b style={{ display: 'block', fontWeight: 600, fontSize: 13.5 }}>{n.title}</b><span style={{ color: 'var(--sf-mid)', fontSize: 12.5 }}>{n.sub}</span></span>
              </button>
            ))}
          </div>
        )}
      </Dropdown>
    </>
  )

  const operatorCaption = isOperator && operator
    ? [operator.service_product, operator.service_environment, operator.key_prefix ? `${operator.key_prefix}…` : null].filter(Boolean).join(' · ')
    : undefined

  const legacyHead = (
    <PageHead
      title={activePage.title}
      sub={activePage.description}
      actions={<PillButton icon={<ArrowPathIcon />} onClick={refreshAll}>Refresh</PillButton>}
    />
  )

  return (
    <DashRoot>
      <div className="sf-container" key={pathname}>
        <TopNav
          homeTo="/developer"
          pathname={navPath}
          items={portalPages.filter(p => !(isOperator && p.title === 'Billing')).map(p => ({ label: p.title, to: p.path }))}
          actions={topActions}
          user={{ name: profile?.name, email: profile?.email, avatarUrl: profile?.avatar_url, caption: operatorCaption || profile?.company || profile?.email || undefined }}
          menuItems={[
            { label: 'Settings', icon: <Cog6ToothIcon />, onClick: () => setShowSettings(true) },
            { label: theme === 'dark' ? 'Light theme' : 'Dark theme', icon: theme === 'dark' ? <SunIcon /> : <MoonIcon />, onClick: handleToggleTheme },
            { label: 'Page builder', icon: <PaintBrushIcon />, to: '/developer/page-builder' },
            { label: 'Documentation', icon: <BookOpenIcon />, to: '/docs' },
            ...(isOperator ? [{ label: 'Review queue', icon: <ClipboardDocumentCheckIcon />, onClick: () => navigate('/admin/verifications') }] : []),
            { label: 'Sign out', icon: <ArrowRightOnRectangleIcon />, onClick: handleLogout },
          ]}
        />

        {activePage.title === 'Dashboard' && !isOperator && hasOrgAdmin === false && !teamBannerDismissed && (
          <DismissibleTeamBanner
            onInvite={() => setShowSettings(true)}
            onDismiss={() => {
              setTeamBannerDismissed(true)
              localStorage.setItem('saflens:team-banner-dismissed', 'true')
            }}
          />
        )}

        {activePage.title === 'Dashboard' && (
          <DeveloperOverview
            token={token}
            stats={stats}
            apiKeys={apiKeys}
            profileName={profile?.name || profile?.email}
            isOperator={isOperator}
            hasOrgAdmin={hasOrgAdmin}
            wallet={wallet}
            refreshVersion={refreshVersion}
            onOpenSettings={() => setShowSettings(true)}
          />
        )}
        {activePage.title === 'Analytics' && (
          <>
            {legacyHead}
            <AnalyticsCharts key={refreshVersion} token={token} />
          </>
        )}
        {activePage.title === 'API Keys' && (
          <>
            {legacyHead}
            <div className="sf-legacy dev-portal">
              <ApiKeysSection
                token={token}
                apiKeys={apiKeys}
                setApiKeys={setApiKeys}
                stats={stats}
                newFullKey={newFullKey}
                setNewFullKey={setNewFullKey}
                onUnauthorized={handleUnauthorized}
                isOperator={isOperator}
              />
            </div>
          </>
        )}
        {activePage.title === 'Webhooks' && (
          <>
            {legacyHead}
            <div className="sf-legacy dev-portal">
              <WebhooksSection key={refreshVersion} token={token} apiKeys={apiKeys} />
            </div>
          </>
        )}
        {activePage.title === 'Billing' && isOperator && (
          <>
            <PageHead title="Billing" sub="Billing is managed by the organisation that owns this service key." />
            <div className="sf-card"><p style={{ margin: 0, color: 'var(--sf-mid)' }}>Service-operator sessions can't view or change the wallet. Ask the organisation owner to sign in to the developer portal.</p></div>
          </>
        )}
        {activePage.title === 'Billing' && !isOperator && (
          <BillingSection key={refreshVersion} wallet={wallet} onWalletChange={setWallet} isOperator={isOperator} />
        )}

        {activePage.title !== 'Dashboard' && activePage.title !== 'Billing' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28 }}>
            <PillLink to="/developer" variant="grey" size="sm">← Back to dashboard</PillLink>
          </div>
        )}
      </div>

      {/* Settings modal — operators get a scoped subset (Branding / Team / Integrations). */}
      {showSettings && (
        <div className="dev-portal" style={{ minHeight: 0 }}>
          <SettingsModal
            token={token}
            isOperator={isOperator}
            onClose={() => setShowSettings(false)}
            onAccountDeleted={handleAccountDeleted}
          />
        </div>
      )}
    </DashRoot>
  )
}
